import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../../providers/LanguageProvider";
import { getSymptoms } from "../../apis/symptom";
import { getMedicines } from "../../apis/medicine";
import { getDisorders } from "../../apis/disorder";
import { getCompanies } from "../../apis/company";
import { getServices } from "../../apis/service";
import { getExpenseCategories } from "../../apis/expenseCategory";
import { getClinicVisitTypes } from "../../apis/visit";
import { getDepartments } from "../../apis/departments";
import { getLabTests, getLabTestCategories } from "../../apis/LabOrder";
import { getRooms, getRoomTypes } from "../../apis/room";
import { getPackages } from "../../apis/packages";
import { getBusinessUnits } from "../../apis/businessUnits";

type MasterCard = {
  key: string;
  label: string;
  route: string;
};

type MasterSection = {
  title: string;
  items: MasterCard[];
};

const MastersDashboard = () => {
  const navigate = useNavigate();
  const { translate: t } = useContext(LanguageContext);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const getTotalFromResponse = (res: any) => {
    if (res?.data?.meta?.total !== undefined) return res.data.meta.total;
    if (res?.meta?.total !== undefined) return res.meta.total;
    if (res?.data?.total !== undefined) return res.data.total;
    if (Array.isArray(res?.data?.data)) return res.data.data.length;
    if (Array.isArray(res?.data)) return res.data.length;
    if (Array.isArray(res)) return res.length;
    return 0;
  };

  const fetchCounts = async () => {
    try {
      const [
        symptomsRes,
        medicinesRes,
        disordersRes,
        companiesRes,
        servicesRes,
        expenseCategoriesRes,
        specialtiesRes,
        departmentsRes,
        packagesRes,
        labTestsRes,
        labCategoriesRes,
        roomTypesRes,
        roomsRes,
        businessUnitsRes,
      ] = await Promise.all([
        getSymptoms({ per_page: 1 }),
        getMedicines({ per_page: 1 }),
        getDisorders({ per_page: 1 }),
        getCompanies({ per_page: "1" }),
        getServices({ per_page: 1 }),
        getExpenseCategories({ per_page: 1 }),
        getClinicVisitTypes(),
        getDepartments({ per_page: 1 }),
        getPackages({ per_page: 1 }),
        getLabTests({ per_page: 1 }),
        getLabTestCategories({ per_page: 1 }),
        getRoomTypes({ per_page: 1 }),
        getRooms({ per_page: 1 }),
        getBusinessUnits({ per_page: 1 }),
      ]);

      setCounts({
        symptoms: getTotalFromResponse(symptomsRes),
        medicines: getTotalFromResponse(medicinesRes),
        disorders: getTotalFromResponse(disordersRes),
        companies: getTotalFromResponse(companiesRes),
        services: getTotalFromResponse(servicesRes),
        expenseCategories: getTotalFromResponse(expenseCategoriesRes),
        specialties: getTotalFromResponse(specialtiesRes),
        departments: getTotalFromResponse(departmentsRes),
        packages: getTotalFromResponse(packagesRes),
        labTests: getTotalFromResponse(labTestsRes),
        labCategories: getTotalFromResponse(labCategoriesRes),
        roomTypes: getTotalFromResponse(roomTypesRes),
        rooms: getTotalFromResponse(roomsRes),
        businessUnits: getTotalFromResponse(businessUnitsRes),
      });
    } catch (error) {
      console.error("Failed to load master counts", error);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const sections: MasterSection[] = [
    {
      title: "Clinical Masters",
      items: [
        { key: "symptoms", label: "symptom_list", route: "/symptoms" },
        { key: "medicines", label: "medicine_list", route: "/medicines" },
        { key: "disorders", label: "disorder_list", route: "/disorders" },
        { key: "companies", label: "company_list", route: "/companies" },
      ],
    },
    {
      title: "Administrative Masters",
      items: [
        { key: "services", label: "service_list", route: "/services" },
        {
          key: "expenseCategories",
          label: "expense_category",
          route: "/expense-categories",
        },
        { key: "specialties", label: "specialities", route: "/visit-types-list" },
        { key: "departments", label: "Departments", route: "/departments" },
        { key: "packages", label: "packages", route: "/packages" },
        { key: "roomTypes", label: "room_types", route: "/room-types" },
        { key: "rooms", label: "rooms", route: "/rooms" },
        { key: "businessUnits", label: "Business Units", route: "/business-units" },
      ],
    },
    {
      title: "Pathological Masters",
      items: [
        { key: "labTests", label: "lab_tests", route: "/test-list" },
        { key: "labCategories", label: "lab_tests_categories", route: "/lab-categories" },
      ],
    },
    {
      title: "Pharmacy Masters",
      items: [],
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Masters
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Master lists grouped by type.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-8">
        {sections.map((section) => (
          <div key={section.title}>
            <h2 className="text-sm font-semibold text-gray-900">
              {section.title}
            </h2>
            {section.items.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No masters available.</p>
            ) : (
              <ul className="grid grid-cols-1 gap-5 mt-4 sm:grid-cols-2 lg:grid-cols-4">
                {section.items.map((item) => (
                  <li
                    key={item.key}
                    className="flex col-span-1 rounded-md shadow-sm cursor-pointer"
                    onClick={() => navigate(item.route)}
                  >
                    <div className="flex items-center justify-center flex-shrink-0 w-16 text-sm font-medium text-white bg-primary rounded-l-md">
                      {String(counts[item.key] ?? 0)}
                    </div>
                    <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
                      <div className="flex-1 px-4 py-2 text-sm truncate">
                        <span className="font-medium text-gray-900 hover:text-gray-600">
                          {t(item.label)}
                        </span>
                        <p className="text-gray-500">Total</p>
                      </div>
                      <div className="flex-shrink-0 pr-2">
                        <button
                          className="inline-flex items-center justify-center w-8 h-8 text-gray-400 bg-transparent bg-white rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          onClick={() => navigate(item.route)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MastersDashboard;
